'use client'

import { useEffect, useRef, useState } from 'react'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@nwwp/ui-shadcn'
import { useScheduleOtp } from '../_hooks/use-schedule-otp'

const formatCountdown = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`

type Props = {
  open: boolean
  phoneNumber: string
  onVerified: () => Promise<void>
}

export function OtpDialog({ open, phoneNumber, onVerified }: Props) {
  const { otpStatus, canResend, resendCountdown, onOtpComplete, resendOtp, resetOtpStatus } =
    useScheduleOtp(phoneNumber)
  const [otpValue, setOtpValue] = useState('')
  const calledRef = useRef(false)

  useEffect(() => {
    if (/^\d{6}$/.test(otpValue) && otpStatus === 'idle') {
      void onOtpComplete(otpValue)
    }
  }, [otpValue, otpStatus, onOtpComplete])

  useEffect(() => {
    if (otpStatus === 'validated' && !calledRef.current) {
      calledRef.current = true
      void onVerified()
      return
    }
    if (otpStatus !== 'invalid') return
    const timeout = setTimeout(() => {
      setOtpValue('')
      resetOtpStatus()
    }, 0)
    return () => clearTimeout(timeout)
  }, [otpStatus, onVerified, resetOtpStatus])

  if (!open) return null

  if (otpStatus === 'validated') {
    return (
      <div role="dialog" aria-modal="true" aria-label="Verifying" className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative flex h-full items-center justify-center px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white px-8 py-10 shadow-xl">
            <p className="text-body text-charcoal">One moment…</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="otp-dialog-title" className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative flex h-full items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white px-8 py-10 shadow-xl">
          <p id="otp-dialog-title" className="text-title-xs mb-2 font-bold text-gray-dark">
            Please enter your 6-digit security code
          </p>
          <p className="mb-6 text-body-sm text-neutral-darker">We sent a verification code to your phone number.</p>

          <div className="mb-2">
            <InputOTP
              maxLength={6}
              value={otpValue}
              onChange={(value) => /^\d*$/.test(value) && setOtpValue(value)}
              disabled={otpStatus === 'loading'}
              autoFocus
            >
              <InputOTPGroup>
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPSlot
                    key={index}
                    index={index}
                    className="h-16 bg-white text-charcoal shadow-[0_0_0_1px_var(--color-smoke)] tablet:h-20"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {otpStatus === 'loading' && <p className="mb-2 text-body-sm text-neutral-darker">Verifying…</p>}

          {otpStatus === 'invalid' && <p className="mb-4 text-body-sm text-red">Invalid code. Please try again.</p>}

          {resendCountdown !== null && (
            <p className="text-body-sm text-neutral-darker">
              Didn&rsquo;t receive your code?{' '}
              {canResend ? (
                <button type="button" onClick={resendOtp} className="text-green underline">
                  Resend
                </button>
              ) : (
                <>Resend in {formatCountdown(resendCountdown)}</>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
